import { Module } from 'vuex';
import roleService, { Role, CreateRoleData, UpdateRoleData } from '@/services/role.service';

export interface RolesState {
  roles: Role[];
  currentRole: Role | null;
  loading: boolean;
  error: string | null;
}

const rolesModule: Module<RolesState, any> = {
  namespaced: true,

  state: (): RolesState => ({
    roles: [],
    currentRole: null,
    loading: false,
    error: null,
  }),

  mutations: {
    SET_ROLES(state, roles: Role[]) {
      state.roles = roles;
    },

    SET_CURRENT_ROLE(state, role: Role | null) {
      state.currentRole = role;
    },

    ADD_ROLE(state, role: Role) {
      state.roles.push(role);
    },

    UPDATE_ROLE(state, updatedRole: Role) {
      const index = state.roles.findIndex(r => r.id === updatedRole.id);
      if (index !== -1) {
        state.roles.splice(index, 1, updatedRole);
      }
    },

    REMOVE_ROLE(state, roleId: string) {
      state.roles = state.roles.filter(r => r.id !== roleId);
    },

    SET_LOADING(state, loading: boolean) {
      state.loading = loading;
    },

    SET_ERROR(state, error: string | null) {
      state.error = error;
    },
  },

  actions: {
    /**
     * Obtener todos los roles
     */
    async fetchRoles({ commit }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const roles = await roleService.getAllRoles();
        commit('SET_ROLES', roles);
        return roles;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al cargar roles';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Obtener rol por ID
     */
    async fetchRoleById({ commit }, roleId: string) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const role = await roleService.getRoleById(roleId);
        commit('SET_CURRENT_ROLE', role);
        return role;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al cargar rol';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Crear nuevo rol
     */
    async createRole({ commit }, roleData: CreateRoleData) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const newRole = await roleService.createRole(roleData);
        commit('ADD_ROLE', newRole);
        return newRole;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al crear rol';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Actualizar rol
     */
    async updateRole({ commit }, { roleId, roleData }: { roleId: string; roleData: UpdateRoleData }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const updatedRole = await roleService.updateRole(roleId, roleData);
        commit('UPDATE_ROLE', updatedRole);
        return updatedRole;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al actualizar rol';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Eliminar rol
     */
    async deleteRole({ commit }, roleId: string) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        await roleService.deleteRole(roleId);
        commit('REMOVE_ROLE', roleId);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Error al eliminar rol';
        commit('SET_ERROR', errorMessage);
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Limpiar error
     */
    clearError({ commit }) {
      commit('SET_ERROR', null);
    },
  },

  getters: {
    allRoles: (state) => state.roles,
    currentRole: (state) => state.currentRole,
    isLoading: (state) => state.loading,
    error: (state) => state.error,
    roleById: (state) => (roleId: string) => state.roles.find(r => r.id === roleId),
    rolesCount: (state) => state.roles.length,
  },
};

export default rolesModule;
